import React, { useState, useCallback, useRef, useEffect } from "react";
import { Box, useMediaQuery, CircularProgress, Skeleton } from "@mui/material";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  OverlayView,
} from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import config from "../../config.js";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router";
import origin_Icon from "../../images/route/origin_icon.svg";
import destination_Icon from "../../images/route/destination_icon.svg";
import intermediate_Icon from "../../images/route/intermediate_icon.svg";
import stops_Icon from "../../images/route/stops_icon.svg";
import StopsSection from "./StopsSection.jsx";
import VehiclesSection from "./VehiclesSection.jsx";
import { useSettings } from "../../context/SettingsContext";
import { useMapIcons } from "../../hooks/useMapIcons.js";

const getContainerStyle = (isMobile) => ({
  width: "100%",
  height: isMobile ? "400px" : "100%",
});
const center = {
  lat: 13.025,
  lng: 77.628,
};

const libraries = ["geometry"];

export default function RouteAddMap({
  stops = [],
  selectedStops = [],
  onStopSelect,
  allStopsData = [],
  routeData = {},
  onRouteDataChange,
  errors,
  vehicles = [],
}) {
  // console.log("RouteAddMap routeData:", routeData);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [mapInstance, setMapInstance] = useState(null);
  const directionsService = useRef(null);
  const [directions, setDirections] = useState(null);
  const [intermediatePointCount, setIntermediatePointCount] = useState(0);

  const { settings } = useSettings();
  const { getOriginStopIcon, getDestinationStopIcon, getIntermediateStopIcon } =
    useMapIcons(settings, [], [], []);

  // Responsive breakpoints
  const isXsScreen = useMediaQuery("(max-width:600px)");
  const isSmScreen = useMediaQuery("(max-width:900px)");
  const isMdScreen = useMediaQuery("(max-width:1200px)");

  // Check if both origin and destination are set
  const isOriginDestinationSet = Boolean(
    routeData.origin && routeData.destination,
  );

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey || "API_KEY_HERE",
    libraries,
  });

  useEffect(() => {
    // This will run once after map and directions service are ready
    if (
      isLoaded &&
      mapInstance &&
      directionsService.current &&
      routeData.origin &&
      routeData.destination &&
      !directions
    ) {
      // Calculate the route specifically for the initial load
      calculateRoute();
    }
  }, [
    isLoaded,
    mapInstance,
    directionsService.current,
    routeData.origin,
    routeData.destination,
  ]);

  const handleStopToggle = (stop) => {
    // Only allow stop selection if origin and destination are set
    if (!isOriginDestinationSet) {
      setErrorMessage(
        "Please select both origin and destination first before adding stops",
      );
      return;
    }

    if (onStopSelect) {
      onStopSelect(stop);
    }
  };

  // Handle removing a stop
  const handleRemoveStop = (stop) => {
    if (onStopSelect) {
      onStopSelect(stop);
    }
  };
  // Handle removing an intermediate point
  const handleRemoveIntermediatePoint = (point, index) => {
    if (routeData.onRouteDataChange) {
      const updatedRoutePoints = routeData.routePoints.filter(
        (p, i) =>
          // Keep all stop points and only remove the specific intermediate point
          p.isStop || p.lat !== point.lat || p.lng !== point.lng,
      );
      routeData.onRouteDataChange("routePoints", updatedRoutePoints);
    }
  };

  // Map initialization
  const onLoad = useCallback(
    function callback(map) {
      setMapInstance(map);
      directionsService.current = new window.google.maps.DirectionsService();

      // If route data already exists, calculate directions immediately
      if (routeData.origin && routeData.destination) {
        // Call your route calculation function here
        calculateRoute();
      }
    },
    [routeData.origin, routeData.destination],
  );

  // Map cleanup
  const onUnmount = useCallback(function callback(map) {
    setMapInstance(null);
    directionsService.current = null;
  }, []);

  // Update intermediate point counter when route points change
  useEffect(() => {
    if (routeData.routePoints) {
      const intermediatePts = routeData.routePoints.filter(
        (point) => !point.isStop,
      );
      setIntermediatePointCount(intermediatePts.length);
    }
  }, [routeData.routePoints]);

  const handleAddRoutePoint = useCallback(
    (e) => {
      try {
        // Only allow adding intermediate points if both origin and destination are set
        if (!isOriginDestinationSet) {
          setErrorMessage(
            "Please select both origin and destination first before adding intermediate points",
          );
          return;
        }

        const clickedLat = e.latLng.lat();
        const clickedLng = e.latLng.lng();

        // Similar logic from old implementation to find where to insert the point
        let insertIndex = 0;
        let minDistance = Infinity;

        let allPoints = [
          {
            lat: Number(routeData.origin.latitude),
            lng: Number(routeData.origin.longitude),
          },
          ...routeData.routePoints,
          {
            lat: Number(routeData.destination.latitude),
            lng: Number(routeData.destination.longitude),
          },
        ].filter((point) => point.lat && point.lng);

        for (let i = 0; i < allPoints.length - 1; i++) {
          const start = new window.google.maps.LatLng(
            allPoints[i].lat,
            allPoints[i].lng,
          );
          const end = new window.google.maps.LatLng(
            allPoints[i + 1].lat,
            allPoints[i + 1].lng,
          );
          const clickedPoint = new window.google.maps.LatLng(
            clickedLat,
            clickedLng,
          );

          const distance =
            window.google.maps.geometry.spherical.computeDistanceBetween(
              clickedPoint,
              new window.google.maps.LatLng(
                (start.lat() + end.lat()) / 2,
                (start.lng() + end.lng()) / 2,
              ),
            );

          if (distance < minDistance) {
            minDistance = distance;
            insertIndex = i;
          }
        }

        // Create a new intermediate point
        const newPoint = {
          lat: clickedLat,
          lng: clickedLng,
          isStop: false,
          stopId: null,
          name: `RouteID.IM${intermediatePointCount + 1}`,
          id: `intermediate-${Date.now()}-${intermediatePointCount + 1}`,
        };

        // Insert the new point at the calculated position
        const updatedRoutePoints = [...routeData.routePoints];
        // Adjust insertIndex to match the routePoints array (not including origin/destination)
        if (insertIndex === 0) {
          // Insert at the beginning
          updatedRoutePoints.unshift(newPoint);
        } else if (insertIndex >= routeData.routePoints.length) {
          // Insert at the end
          updatedRoutePoints.push(newPoint);
        } else {
          // Insert in the middle
          updatedRoutePoints.splice(insertIndex, 0, newPoint);
        }

        // Update parent component with new route points
        if (routeData.onRouteDataChange) {
          routeData.onRouteDataChange("routePoints", updatedRoutePoints);
        }
      } catch (error) {
        // console.error("Error handling map click:", error);
        setErrorMessage("An error occurred. Please refresh the page.");
      }
    },
    [routeData, intermediatePointCount, isOriginDestinationSet],
  );

  const calculateRoute = async () => {
    try {
      // Sort waypoints in the order they should appear on the route
      const sortedPoints = [...routeData.routePoints];

      // Check if we have too many waypoints
      if (sortedPoints.length > 25) {
        setErrorMessage(
          `Maximum of 25 waypoints allowed. You currently have ${sortedPoints.length}. Please remove some intermediate points.`,
        );
        return; // Exit early, don't attempt to calculate route
      }
      // Create waypoints from sorted points - this is critical for proper route display
      const waypoints = sortedPoints.map((point) => ({
        location: { lat: point.lat, lng: point.lng },
        stopover: Boolean(point.isStop),
      }));

      const request = {
        origin: {
          lat: Number(routeData.origin.latitude),
          lng: Number(routeData.origin.longitude),
        },
        destination: {
          lat: Number(routeData.destination.latitude),
          lng: Number(routeData.destination.longitude),
        },
        waypoints: waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false, // Keep this false to maintain waypoint order
      };

      // Always request directions if origin and destination exist,
      // even if there are no waypoints
      directionsService.current.route(request, (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
          setErrorMessage("");
        } else {
          // console.error(`Directions request failed due to ${status}`);
          setErrorMessage(
            "Could not calculate route. Please check your waypoints.",
          );
          setDirections(null);
        }
      });
    } catch (error) {
      // console.error("Error calculating route:", error);
      setErrorMessage("An error occurred while calculating the route.");
      setDirections(null);
    }
  };
  // Calculate and display route whenever origin, destination or route points change
  useEffect(() => {
    if (
      isLoaded &&
      directionsService.current &&
      routeData.origin &&
      routeData.destination
    ) {
      // Always call calculateRoute when origin and destination exist
      calculateRoute();
    } else if (!routeData.origin || !routeData.destination) {
      setDirections(null);
    }
  }, [
    isLoaded,
    directionsService.current,
    routeData.origin,
    routeData.destination,
    routeData.routePoints,
  ]);
  // Fit map bounds to include all route points
  const fitBounds = useCallback(() => {
    if (mapInstance && routeData.origin && routeData.destination) {
      const bounds = new window.google.maps.LatLngBounds();

      // Add origin to bounds
      bounds.extend(
        new window.google.maps.LatLng(
          Number(routeData.origin.latitude),
          Number(routeData.origin.longitude),
        ),
      );

      // Add destination to bounds
      bounds.extend(
        new window.google.maps.LatLng(
          Number(routeData.destination.latitude),
          Number(routeData.destination.longitude),
        ),
      );

      // Add all route points to bounds
      routeData.routePoints.forEach((point) => {
        if (point.lat && point.lng) {
          bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
        }
      });

      mapInstance.fitBounds(bounds);
    }
  }, [
    mapInstance,
    routeData.origin,
    routeData.destination,
    routeData.routePoints,
  ]);

  // Fit bounds whenever relevant data changes
  useEffect(() => {
    if (isLoaded && mapInstance && routeData.origin && routeData.destination) {
      fitBounds();
    }
  }, [
    isLoaded,
    mapInstance,
    fitBounds,
    routeData.origin,
    routeData.destination,
  ]);

  const filteredDestinationStops =
    routeData.routeType === "UNI_DIRCTIONAL" && routeData.origin
      ? stops.filter((stop) => stop.id !== routeData.origin.id)
      : stops;

  // Filter stops for origin to exclude the selected destination when routeType is UNI_DIRCTIONAL
  const filteredOriginStops =
    routeData.routeType === "UNI_DIRCTIONAL" && routeData.destination
      ? stops.filter((stop) => stop.id !== routeData.destination.id)
      : stops;

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>;
  }

  // Fix the loading condition - add parentheses or brackets
  if (!isLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmScreen ? "column" : "row",
          gap: 2,
          mt: 3,
          px: { xs: 1, sm: 2, md: 5 },
          position: "relative",
          width: "100%",
          justifyContent: "center",
        }}
      >
        <Skeleton
          variant="rectangular"
          width="100%"
          height="30vh"
          sx={{ mb: 3, borderRadius: 2 }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isSmScreen ? "column" : "row",
        gap: 2,
        mt: 1,
        // px: { xs: 1, sm: 2, md: 5 },
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      {/* Map Section */}
      <Box
        sx={{
          width: "100%",
          // height: isXsScreen ? '350px' : isSmScreen ? '400px' : '508px',
          position: "relative",
          border: "1px solid #183059",
          overflow: "hidden",
          minHeight: isXsScreen ? "350px" : isSmScreen ? "400px" : "508px",
        }}
      >
        {errorMessage && (
          <div
            style={{
              color: "red",
              margin: "10px 0",
              padding: "10px",
              backgroundColor: "#ffeeee",
              borderRadius: "5px",
              position: "absolute",
              zIndex: 10,
              top: 0,
              left: 0,
              right: 0,
            }}
          >
            {errorMessage}
          </div>
        )}
        <GoogleMap
          mapContainerStyle={getContainerStyle(isXsScreen || isSmScreen)}
          center={center}
          zoom={14}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleAddRoutePoint}
        >
          {/* Origin Marker */}
          {routeData.origin && (
            <>
              <Marker
                position={{
                  lat: Number(routeData.origin.latitude),
                  lng: Number(routeData.origin.longitude),
                }}
                icon={getOriginStopIcon() || origin_Icon}
                title={routeData.origin.name}
              />
              <OverlayView
                position={{
                  lat: Number(routeData.origin.latitude),
                  lng: Number(routeData.origin.longitude),
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={(width, height) => ({
                  x: -width / 2,
                  y: -height - 42,
                })}
              >
                <div
                  style={{
                    position: "relative",
                    textAlign: "center",
                    width: "112px",
                    height: "18px",
                  }}
                >
                  <svg
                    width="112"
                    height="18"
                    viewBox="0 0 112 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ position: "absolute", top: 0, left: 0 }}
                  >
                    <path
                      d="M10.0071 7.63294C10.3587 3.98124 12.6 8.90272e-07 16.2686 1.19277e-06C37.966 2.98184e-06 90.0453 9.33625e-07 106.329 2.24047e-07C109.09 1.03714e-07 111.281 2.23858 111.281 5V13C111.281 15.7614 109.043 18 106.281 18H0.683594C0.683594 18 9.27312 15.2578 10.0071 7.63294Z"
                      fill="#20A8E0"
                    />
                  </svg>
                  <div
                    style={{
                      position: "absolute",
                      width: "100%",
                      textAlign: "center",
                      lineHeight: "18px",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "12px",
                      padding: "0 8px",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {routeData.origin.name}
                  </div>
                </div>
              </OverlayView>
            </>
          )}

          {/* Destination Marker */}
          {routeData.destination && (
            <>
              <Marker
                position={{
                  lat: Number(routeData.destination.latitude),
                  lng: Number(routeData.destination.longitude),
                }}
                icon={getDestinationStopIcon() || destination_Icon}
                title={routeData.destination.name}
              />
              <OverlayView
                position={{
                  lat: Number(routeData.destination.latitude),
                  lng: Number(routeData.destination.longitude),
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={(width, height) => ({
                  x: -width / 2,
                  y: -height - 42,
                })}
              >
                <div
                  style={{
                    position: "relative",
                    textAlign: "center",
                    width: "112px",
                    height: "18px",
                  }}
                >
                  <svg
                    width="112"
                    height="18"
                    viewBox="0 0 112 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ position: "absolute", top: 0, left: 0 }}
                  >
                    <path
                      d="M10.0071 7.63294C10.3587 3.98124 12.6 8.90272e-07 16.2686 1.19277e-06C37.966 2.98184e-06 90.0453 9.33625e-07 106.329 2.24047e-07C109.09 1.03714e-07 111.281 2.23858 111.281 5V13C111.281 15.7614 109.043 18 106.281 18H0.683594C0.683594 18 9.27312 15.2578 10.0071 7.63294Z"
                      fill="#EE3B2B"
                    />
                  </svg>
                  <div
                    style={{
                      position: "absolute",
                      width: "100%",
                      textAlign: "center",
                      lineHeight: "18px",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "12px",
                      padding: "0 8px",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {routeData.destination.name}
                  </div>
                </div>
              </OverlayView>
            </>
          )}

          {/* Display all route points with appropriate icons */}
          {routeData.routePoints &&
            routeData.routePoints.map((point, index) => (
              <React.Fragment
                key={`point-${index}-${point.id || point.stopId || Date.now()}`}
              >
                <Marker
                  position={{ lat: point.lat, lng: point.lng }}
                  icon={
                    point.isStop
                      ? getIntermediateStopIcon() || stops_Icon
                      : intermediate_Icon
                  }
                  title={
                    point.name ||
                    (point.isStop ? `Stop ${index}` : `IM${index}`)
                  }
                />

                {/* Add simple label ONLY for stops (not intermediate points) */}
                {point.isStop && (
                  <OverlayView
                    position={{ lat: point.lat, lng: point.lng }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={(width, height) => ({
                      x: -width / 2,
                      y: -height - 35,
                    })}
                  >
                    <div
                      style={{
                        backgroundColor: "#fff",
                        width: " max-content",
                        color: "black",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      }}
                    >
                      {point.name || `Stop ${index + 1}`}
                    </div>
                  </OverlayView>
                )}
              </React.Fragment>
            ))}

          {/* Render the direction route */}
          {directions && (
            <DirectionsRenderer
              key={`directions-${routeData.routePoints?.length || 0}`}
              directions={directions}
              options={{
                suppressMarkers: true,
                preserveViewport: true,
                polylineOptions: {
                  strokeColor: routeData.routeColor || "#20A8E0",
                  strokeWeight: 5,
                },
              }}
            />
          )}
        </GoogleMap>
      </Box>

      {/* Stops Section */}
      {/* <StopsSection
        stops={stops}
        selectedStops={selectedStops}
        routeData={routeData}
        onRouteDataChange={onRouteDataChange}
        onStopToggle={handleStopToggle}
        onRemoveStop={handleRemoveStop}
        onRemoveIntermediatePoint={handleRemoveIntermediatePoint}
        errors={errors}
        isOriginDestinationSet={isOriginDestinationSet}
      /> */}

      {/* <VehiclesSection
        routeData={routeData}
        vehicles={vehicles}
        onVehicleChange={(selectedVehicles) =>
          onRouteDataChange("selectedVehicles", selectedVehicles)
        }
      /> */}
    </Box>
  );
}
